import { computed, shallowRef, watch } from "vue";
import { fromMaybe, isNothing, type Maybe } from "../lib/maybe";
import { assertNever } from "../lib/assertNever";
import { defineStore } from "pinia";
import type { EntityFrontmatter } from "../lib/entityFrontmatter";
import { createTimeoutHandle } from "../lib/timeoutHandle";
import { updateProjectDatabaseEntryFields } from "../lib/tauri/database";
import { useEditorHeadingsStore } from "./useEditorHeadingsStore";
import { useEditorSessionStore } from "./useEditorSessionStore";
import { useOutlineEntitiesStore } from "./useOutlineEntitiesStore";
import {
  fieldsForLoadedEntry,
  initialActiveEntityFieldsModel,
  updateActiveEntityFields,
  type ActiveEntityFieldsCmd,
  type ActiveEntityFieldsMsg,
} from "./useActiveEntityFieldsStore.update";

const SAVE_DEBOUNCE_MS = 1000;

/** Shared draft of the active worldbuilding entry's typed frontmatter — one
 * source of truth for `EntityFieldsPanel.vue` (attributes) and
 * `EntityLinksPanel.vue` (relationships) so debounced attribute saves never
 * clobber link edits (or vice versa).
 *
 * TEA: pure Model/Msg in `useActiveEntityFieldsStore.update.ts`. */
export const useActiveEntityFieldsStore = defineStore(
  "activeEntityFields",
  () => {
    const session = useEditorSessionStore();
    const outlineEntities = useOutlineEntitiesStore();
    const headings = useEditorHeadingsStore();

    const model = shallowRef(initialActiveEntityFieldsModel);
    const saveTimer = createTimeoutHandle();

    function runCmd(cmd: ActiveEntityFieldsCmd): void {
      switch (cmd.kind) {
        case "scheduleSave": {
          const target = model.value.loadedFor;
          if (isNothing(target)) return;
          const snapshot = model.value.fields;
          saveTimer.schedule(SAVE_DEBOUNCE_MS, () => {
            void updateProjectDatabaseEntryFields(
              target.value.rootPath,
              target.value.name,
              snapshot,
            ).then(async () => {
              await outlineEntities.refreshDatabase();
              headings.scheduleEntityOccurrencesRefresh();
              headings.scheduleHarperLexiconRefresh();
            });
          });
          return;
        }
        case "flushSave":
          saveTimer.flush();
          return;
        case "refreshLexicon":
          headings.scheduleEntityOccurrencesRefresh();
          headings.scheduleHarperLexiconRefresh();
          return;
        default:
          assertNever(cmd);
      }
    }

    function dispatch(msg: ActiveEntityFieldsMsg): void {
      const [next, cmds] = updateActiveEntityFields(model.value, msg);
      model.value = next;
      for (const cmd of cmds) {
        runCmd(cmd);
      }
    }

    const fields = computed(() => model.value.fields);

    function flushPending(): void {
      saveTimer.flush();
    }

    async function loadFields(): Promise<void> {
      flushPending();
      const rootPath = fromMaybe(session.rootPath);
      const name = session.activeDatabase;
      if (rootPath === null || isNothing(name) || session.activeMap) {
        dispatch({ type: "Cleared" });
        return;
      }
      const { getProjectDatabaseEntryFields } =
        await import("../lib/tauri/database");
      const loaded = await getProjectDatabaseEntryFields(rootPath, name.value);
      if (
        fromMaybe(session.rootPath) !== rootPath ||
        isNothing(session.activeDatabase) ||
        session.activeDatabase.value !== name.value
      ) {
        return;
      }
      dispatch({
        type: "Loaded",
        target: { rootPath, name: name.value },
        fields: loaded,
      });
    }

    watch(
      [
        () => fromMaybe(session.rootPath),
        () => session.activeDatabase,
        () => session.activeMap,
      ],
      () => void loadFields(),
      { immediate: true },
    );

    /** In-memory fields for the open entry — used before debounced save lands. */
    function fieldsForEntry(
      rootPath: string,
      name: string,
    ): Maybe<EntityFrontmatter> {
      return fieldsForLoadedEntry(model.value, rootPath, name);
    }

    function setField<K extends keyof EntityFrontmatter>(
      key: K,
      value: EntityFrontmatter[K],
      options?: { immediate?: boolean },
    ): void {
      dispatch({
        type: "SetField",
        key,
        value,
        immediate: options?.immediate === true,
      });
    }

    return {
      fields,
      fieldsForEntry,
      setField,
      flushPending,
      loadFields,
    };
  },
);
